import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import {
  fetchAllReferrals,
  fetchReferrerDetail,
  fetchReferralDetail,
  fetchReferralLink,
  retagReferral,
} from "../../actions/referralActions";
import useHasPermission from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissions";
import {
  modalOverlay,
  modalBox,
  modalHeader,
  modalBody,
  closeBtn,
  downloadBtnStyle,
  downloadCSV,
} from "./shared";

const AllReferrals = ({ stateLabels, typeLabels, typeOptions, fromDate, toDate }) => {
  const dispatch = useDispatch();
  const canRetagReferrals = useHasPermission(PERMISSIONS.RETAG_REFERRALS);

  const [allReferralsPage, setAllReferralsPage] = useState(1);
  const [filterReferrerId, setFilterReferrerId] = useState("");
  const [filterRefereeId, setFilterRefereeId] = useState("");
  const [filterReferrerTypeId, setFilterReferrerTypeId] = useState("");
  const [filterRefereeTypeId, setFilterRefereeTypeId] = useState("");

  const [referrerModal, setReferrerModal] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [referralModal, setReferralModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [copyingTag, setCopyingTag] = useState(null);

  const [retagModal, setRetagModal] = useState(false);
  const [retagPennTags, setRetagPennTags] = useState("");
  const [retagAccountNumbers, setRetagAccountNumbers] = useState("");
  const [retagNewReferrer, setRetagNewReferrer] = useState("");

  const { loading: allReferralsLoading, data: allReferralsData, error: allReferralsError } =
    useSelector((state) => state.getAllReferrals) || {};
  const { loading: retagLoading, success: retagSuccess, error: retagError } =
    useSelector((state) => state.retagReferral) || {};
  const { loading: detailLoading, data: detailData, error: detailError } =
    useSelector((state) => state.getReferrerDetail) || {};
  const { loading: referralDetailLoading, data: referralDetailData, error: referralDetailError } =
    useSelector((state) => state.getReferralDetail) || {};
  const { loading: linkLoading, data: linkData, error: linkError } =
    useSelector((state) => state.getReferralLink) || {};

  const allReferrals = allReferralsData?.data?.referrals;
  const detail = detailData?.data;
  const referralDetail = referralDetailData?.data;
  const referralLink = linkData?.data?.referral_link;

  const handleAllReferralsPageChange = (page) => {
    setAllReferralsPage(page);
    dispatch(fetchAllReferrals(page, 100, fromDate, toDate, filterReferrerId, filterRefereeId, filterReferrerTypeId, filterRefereeTypeId));
  };

  const handleAllReferralsFilter = () => {
    setAllReferralsPage(1);
    dispatch(fetchAllReferrals(1, 100, fromDate, toDate, filterReferrerId, filterRefereeId, filterReferrerTypeId, filterRefereeTypeId));
  };

  const handleClearAllReferralsFilters = () => {
    setFilterReferrerId("");
    setFilterRefereeId("");
    setFilterReferrerTypeId("");
    setFilterRefereeTypeId("");
    setAllReferralsPage(1);
    dispatch(fetchAllReferrals(1, 100, fromDate, toDate));
  };

  const handleViewReferrer = (item) => {
    setSelectedReferrer(item);
    dispatch(fetchReferrerDetail(item.referrer_id));
    dispatch(fetchReferralLink(item.referrer_tag));
    setReferrerModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleViewReferral = (item) => {
    setSelectedReferral(item);
    dispatch(fetchReferralDetail(item.referrer_id, item.referee_id));
    setReferralModal(true);
  };

  const handleRetagSubmit = () => {
    const pennTagsList = retagPennTags.split(",").map((t) => t.trim()).filter(Boolean);
    const acctNumList = retagAccountNumbers.split(",").map((t) => t.trim()).filter(Boolean);
    if (!pennTagsList.length && !acctNumList.length) {
      toast.error("Enter at least one PennTag or account number");
      return;
    }
    if (!retagNewReferrer.trim()) {
      toast.error("Referrer tag is required");
      return;
    }
    dispatch(retagReferral({ referrer_tag: retagNewReferrer.trim(), penntags: pennTagsList, account_numbers: acctNumList }));
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      toast.success("Referral link copied!");
    });
  };

  const handleCopyStaffLink = async (referrerTag) => {
    setCopyingTag(referrerTag);
    try {
      const response = await dispatch(fetchReferralLink(referrerTag));
      const link = response?.data?.referral_link;
      if (link) {
        await navigator.clipboard.writeText(link);
        toast.success("Referral link copied!");
      } else {
        toast.error("No referral link found");
      }
    } catch (err) {
      toast.error("Could not fetch referral link");
    } finally {
      setCopyingTag(null);
    }
  };

  const handleDownloadAllReferrals = () => {
    if (!allReferrals?.items?.length) {
      toast.warning("No referrals data to download");
      return;
    }
    downloadCSV(
      "all_referrals",
      ["Referrer ID", "Referrer Tag", "Referrer Name", "Referrer Type", "Referee ID", "Referee Tag", "Referee Name", "Referee Type", "State", "Referral Point", "Point Credited"],
      allReferrals.items.map((i) => [
        i.referrer_id, i.referrer_tag, i.referrer_name || "",
        typeLabels[i.referrer_type_id] || i.referrer_type_id,
        i.referee_id, i.referee_tag, i.referee_name || "",
        typeLabels[i.referee_type_id] || i.referee_type_id,
        stateLabels[i.state]?.label || i.state,
        i.referral_point, i.is_point_credited ? "Yes" : "No",
      ]),
    );
  };

  const closeReferrerModal = () => {
    setReferrerModal(false);
    setSelectedReferrer(null);
    setReferralModal(false);
    setSelectedReferral(null);
    document.body.style.overflow = "auto";
  };

  const closeReferralModal = () => {
    setReferralModal(false);
    setSelectedReferral(null);
  };

  useEffect(() => {
    if (retagSuccess) {
      toast.success("Referrals retagged successfully");
      setRetagPennTags("");
      setRetagAccountNumbers("");
      setRetagNewReferrer("");
      setRetagModal(false);
      dispatch(fetchAllReferrals(1, 100));
      dispatch({ type: "RETAG_REFERRAL_RESET" });
    }
    if (retagError) {
      toast.error(retagError);
      dispatch({ type: "RETAG_REFERRAL_RESET" });
    }
  }, [retagSuccess, retagError, dispatch]);

  return (
    <>
      {/* Filters */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <label style={{ display: "block", fontSize: "13px", lineHeight: "1.5", color: "#fff", marginBottom: "4px" }}>Referrer ID</label>
            <input type="number" className="invest-input" placeholder="e.g. 16" value={filterReferrerId} onChange={(e) => setFilterReferrerId(e.target.value)} style={{ width: "130px", height: "42px" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "13px", lineHeight: "1.5", color: "#fff", marginBottom: "4px" }}>Referee ID</label>
            <input type="number" className="invest-input" placeholder="e.g. 36" value={filterRefereeId} onChange={(e) => setFilterRefereeId(e.target.value)} style={{ width: "130px", height: "42px" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "13px", lineHeight: "1.5", color: "#fff", marginBottom: "4px" }}>Referrer Type</label>
            <select className="invest-input" value={filterReferrerTypeId} onChange={(e) => setFilterReferrerTypeId(e.target.value)} style={{ width: "150px", height: "42px", color: "#fff", background: "#1a1a1a" }}>
              <option value="" style={{ background: "#1a1a1a", color: "#fff" }}>All</option>
              {typeOptions.map((t) => (
                <option key={t.id} value={t.id} style={{ background: "#1a1a1a", color: "#fff" }}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "13px", lineHeight: "1.5", color: "#fff", marginBottom: "4px" }}>Referee Type</label>
            <select className="invest-input" value={filterRefereeTypeId} onChange={(e) => setFilterRefereeTypeId(e.target.value)} style={{ width: "150px", height: "42px", color: "#fff", background: "#1a1a1a" }}>
              <option value="" style={{ background: "#1a1a1a", color: "#fff" }}>All</option>
              {typeOptions.map((t) => (
                <option key={t.id} value={t.id} style={{ background: "#1a1a1a", color: "#fff" }}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: "13px", lineHeight: "1.5", marginBottom: "4px", visibility: "hidden" }}>&nbsp;</label>
            <button className="login-btn" onClick={handleAllReferralsFilter} style={{ padding: "0 20px", height: "42px", width: "auto", marginLeft: "12px" }}>Apply</button>
          </div>
          {(filterReferrerId || filterRefereeId || filterReferrerTypeId || filterRefereeTypeId) && (
            <div>
              <label style={{ display: "block", fontSize: "13px", lineHeight: "1.5", marginBottom: "4px", visibility: "hidden" }}>&nbsp;</label>
              <button onClick={handleClearAllReferralsFilters} style={{ padding: "0 16px", height: "42px", background: "transparent", border: "1px solid #666", borderRadius: "4px", cursor: "pointer", color: "#fff", fontSize: "14px", whiteSpace: "nowrap" }}>Clear Filters</button>
            </div>
          )}
        </div>
      </div>

      {allReferralsLoading ? (
        <Loader />
      ) : allReferralsError ? (
        <p className="text-danger text-center">{allReferralsError}</p>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            {canRetagReferrals && (
              <button onClick={() => setRetagModal(true)} style={{ ...downloadBtnStyle, background: "#007bff", color: "#fff" }}>
                Retag Referrals
              </button>
            )}
            <button onClick={handleDownloadAllReferrals} style={downloadBtnStyle}>⬇ Download CSV</button>
          </div>

          {retagModal && (
            <div style={modalOverlay}>
              <div style={{ ...modalBox, background: "#1a1a1a", maxWidth: "500px" }}>
                <div style={modalHeader}>
                  <h3 style={{ color: "#fff", margin: 0, fontSize: "18px" }}>Retag Referrals</h3>
                  <button style={closeBtn} onClick={() => setRetagModal(false)}>×</button>
                </div>
                <div style={modalBody}>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", color: "#aaa", fontSize: "13px", marginBottom: "6px" }}>PennTags (comma-separated)</label>
                    <textarea rows={3} placeholder="e.g. tag1, tag2, tag3" value={retagPennTags} onChange={(e) => setRetagPennTags(e.target.value)} style={{ width: "100%", background: "#111", border: "1px solid #444", borderRadius: "8px", padding: "10px 12px", color: "#fff", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", color: "#aaa", fontSize: "13px", marginBottom: "6px" }}>Account Numbers (comma-separated)</label>
                    <textarea rows={3} placeholder="e.g. 0123456789, 0987654321" value={retagAccountNumbers} onChange={(e) => setRetagAccountNumbers(e.target.value)} style={{ width: "100%", background: "#111", border: "1px solid #444", borderRadius: "8px", padding: "10px 12px", color: "#fff", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label style={{ display: "block", color: "#aaa", fontSize: "13px", marginBottom: "6px" }}>New Referrer Tag</label>
                    <input type="text" placeholder="e.g. STAFFTAG" value={retagNewReferrer} onChange={(e) => setRetagNewReferrer(e.target.value)} style={{ width: "100%", background: "#111", border: "1px solid #444", borderRadius: "8px", padding: "10px 12px", color: "#fff", fontSize: "14px", boxSizing: "border-box" }} />
                  </div>
                  <button className="login-btn" onClick={handleRetagSubmit} disabled={retagLoading || (!retagPennTags.trim() && !retagAccountNumbers.trim()) || !retagNewReferrer.trim()} style={{ width: "100%" }}>
                    {retagLoading ? "Submitting..." : "Submit Retag"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <MDBTable responsive style={{ width: "100%", fontSize: "14px" }}>
            <MDBTableHead>
              <tr>
                <th>Referrer Tag</th>
                <th>Referrer Name</th>
                <th>Referrer Type</th>
                <th>Referee Tag</th>
                <th>Referee Name</th>
                <th>Referee Type</th>
                <th>State</th>
                <th>Point</th>
                <th>Credited</th>
                <th>Action</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {allReferrals?.items?.length > 0 ? (
                allReferrals.items.map((item, idx) => (
                  <tr key={`${item.referrer_id}-${item.referee_id}-${idx}`}>
                    <td>{item.referrer_tag}</td>
                    <td>{item.referrer_name || "-"}</td>
                    <td>{typeLabels[item.referrer_type_id] || "-"}</td>
                    <td>{item.referee_tag}</td>
                    <td>{item.referee_name || "-"}</td>
                    <td>{typeLabels[item.referee_type_id] || "-"}</td>
                    <td>
                      {stateLabels[item.state] ? (
                        <span style={{ color: stateLabels[item.state].color, fontWeight: "600" }}>
                          {stateLabels[item.state].label}
                        </span>
                      ) : (
                        item.state
                      )}
                    </td>
                    <td>{item.referral_point}</td>
                    <td>
                      <span style={{ color: item.is_point_credited ? "#28a745" : "#aaa" }}>
                        {item.is_point_credited ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>
                      <p className="withdrawal-btn" onClick={() => handleViewReferral(item)} style={{ cursor: "pointer", color: "white", width: "80px", textAlign: "center", margin: 0 }}>
                        View
                      </p>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "20px" }}>No referrals found</td>
                </tr>
              )}
            </MDBTableBody>
          </MDBTable>

          {allReferrals?.page?.total_page > 1 && (
            <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px" }}>
              {Array.from({ length: allReferrals.page.total_page }, (_, i) => i + 1).map((page) => (
                <button key={page} onClick={() => handleAllReferralsPageChange(page)} style={{ padding: "6px 12px", border: "1px solid #444", borderRadius: "4px", background: allReferralsPage === page ? "#007bff" : "#1a1a1a", color: "#fff", cursor: "pointer" }}>
                  {page}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Referrer Detail Modal */}
      {referrerModal && (
        <div style={modalOverlay} onClick={closeReferrerModal}>
          <div style={modalBox} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>Referrer Detail — {selectedReferrer?.referrer_tag}</h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px" }}>
                  {selectedReferrer?.referrer_name && `${selectedReferrer.referrer_name} · `}
                  ID: {selectedReferrer?.referrer_id}
                </p>
              </div>
              <button style={closeBtn} onClick={closeReferrerModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              {detailLoading ? (
                <Loader />
              ) : detailError ? (
                <p className="text-danger text-center">{detailError}</p>
              ) : detail ? (
                <>
                  <div style={{ background: "#2a2a2a", borderRadius: "8px", padding: "14px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: "11px", color: "#aaa", margin: "0 0 4px" }}>Referral Link</p>
                      {linkLoading ? (
                        <span style={{ fontSize: "13px", color: "#888" }}>Loading...</span>
                      ) : linkError ? (
                        <span style={{ fontSize: "13px", color: "#dc3545" }}>{linkError}</span>
                      ) : (
                        <span style={{ fontSize: "13px", color: "#fff", wordBreak: "break-all" }}>{referralLink || "-"}</span>
                      )}
                    </div>
                    {referralLink && (
                      <button onClick={() => handleCopyLink(referralLink)} style={{ padding: "6px 14px", background: "#007bff", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>Copy</button>
                    )}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "24px" }}>
                    {[
                      { label: "Total Referrals", value: detail.overview?.total_referrals, color: "#007bff" },
                      { label: "Completed", value: detail.overview?.completed_referrals, color: "#28a745" },
                      { label: "Pending", value: detail.overview?.pending_referrals, color: "#ffc107" },
                      { label: "Releasable Points", value: detail.overview?.releasable_referral_points, color: "#6f42c1" },
                    ].map((card) => (
                      <div key={card.label} style={{ background: "#2a2a2a", borderRadius: "8px", padding: "14px", borderLeft: `4px solid ${card.color}` }}>
                        <p style={{ fontSize: "11px", color: "#aaa", margin: 0 }}>{card.label}</p>
                        <h4 style={{ margin: "4px 0 0", color: card.color }}>{card.value ?? "-"}</h4>
                      </div>
                    ))}
                  </div>

                  <p style={{ fontWeight: "600", fontSize: "14px", marginBottom: "10px" }}>Referrals List</p>
                  <MDBTable responsive style={{ fontSize: "13px" }}>
                    <MDBTableHead>
                      <tr>
                        <th>Referee Name</th>
                        <th>Referee Tag</th>
                        <th>Points</th>
                        <th>Status</th>
                        <th>Credited</th>
                        <th>Action</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {detail.referrals_list?.items?.length > 0 ? (
                        detail.referrals_list.items.map((item) => (
                          <tr key={`${item.referrer_id}-${item.referee_id}`}>
                            <td>{item.referee_name}</td>
                            <td>{item.referee_tag}</td>
                            <td>{item.referral_point}</td>
                            <td>
                              <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", background: `${stateLabels[item.state]?.color || "#888"}33`, color: stateLabels[item.state]?.color || "#888" }}>
                                {stateLabels[item.state]?.label ?? item.state}
                              </span>
                            </td>
                            <td>
                              <span style={{ color: item.is_point_credited ? "#28a745" : "#dc3545", fontWeight: "600" }}>
                                {item.is_point_credited ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>
                              <p className="withdrawal-btn" onClick={() => handleViewReferral(item)} style={{ cursor: "pointer", color: "white", width: "70px", textAlign: "center", fontSize: "12px" }}>Details</p>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>No referrals found</td>
                        </tr>
                      )}
                    </MDBTableBody>
                  </MDBTable>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Referral Detail Modal */}
      {referralModal && (
        <div style={{ ...modalOverlay, zIndex: 1100 }} onClick={closeReferralModal}>
          <div style={{ ...modalBox, maxWidth: "520px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>Referral Detail</h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px" }}>
                  {selectedReferral?.referrer_tag} → {selectedReferral?.referee_tag}
                </p>
              </div>
              <button style={closeBtn} onClick={closeReferralModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              {referralDetailLoading ? (
                <Loader />
              ) : referralDetailError ? (
                <p className="text-danger text-center">{referralDetailError}</p>
              ) : referralDetail ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {[
                    { label: "Referee Name", value: referralDetail.referee_name },
                    { label: "Referee Tag", value: referralDetail.referee_tag },
                    { label: "Referrer Tag", value: referralDetail.referrer_tag },
                    { label: "Referral Points", value: referralDetail.referral_point },
                    {
                      label: "Status",
                      value: (
                        <span style={{ padding: "3px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", background: `${stateLabels[referralDetail.state]?.color || "#888"}33`, color: stateLabels[referralDetail.state]?.color || "#888" }}>
                          {stateLabels[referralDetail.state]?.label ?? referralDetail.state}
                        </span>
                      ),
                    },
                    {
                      label: "Point Credited",
                      value: (
                        <span style={{ color: referralDetail.is_point_credited ? "#28a745" : "#dc3545", fontWeight: "600" }}>
                          {referralDetail.is_point_credited ? "Yes" : "No"}
                        </span>
                      ),
                    },
                  ].map((row) => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#2a2a2a", borderRadius: "8px" }}>
                      <span style={{ fontSize: "13px", color: "#aaa", fontWeight: "500" }}>{row.label}</span>
                      <span style={{ fontSize: "14px", fontWeight: "600" }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AllReferrals;
